import { useEffect, useCallback } from 'react';

/**
 * Global keyboard shortcuts for the POS screen.
 *
 * Shortcuts:
 *   F2          — open checkout dialog
 *   Escape      — close any open dialog
 *   /           — focus search input (when not already in an input)
 *   Ctrl+Backspace — clear cart (with confirmation)
 */
export default function useKeyboardShortcuts({
  onCheckout,
  onCloseDialog,
  onFocusSearch,
  onClearCart,
}) {
  const handler = useCallback(
    (e) => {
      const tag = e.target.tagName;
      const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable;

      // F2 — open checkout
      if (e.key === 'F2') {
        e.preventDefault();
        onCheckout?.();
        return;
      }

      // Escape — close dialog
      if (e.key === 'Escape') {
        onCloseDialog?.();
        return;
      }

      // / — focus search (only when not in an input)
      if (e.key === '/' && !isInput) {
        e.preventDefault();
        onFocusSearch?.();
        return;
      }

      // Ctrl+Backspace — clear cart
      if (e.key === 'Backspace' && (e.ctrlKey || e.metaKey) && !isInput) {
        e.preventDefault();
        onClearCart?.();
      }
    },
    [onCheckout, onCloseDialog, onFocusSearch, onClearCart],
  );

  useEffect(() => {
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handler]);
}
